// src/pages/Consultation/components/AdjustmentApprovalModal.jsx - SIMPLIFIED
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { FiX, FiCheck, FiAlertCircle } from "react-icons/fi";
import toast from "react-hot-toast";
import Button from "../../../components/common/Button";
import {
  approvePriceAdjustmentThunk,
  rejectPriceAdjustmentThunk,
  calculatePricingThunk,
} from "../../../store/thunks/consultationThunks";

const AdjustmentApprovalModal = ({
  isOpen,
  onClose,
  pendingAdjustments = [],
  sessionId,
  adminInfo,
}) => {
  const dispatch = useDispatch();
  const [processing, setProcessing] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  if (!isOpen) return null;

  const handleApprove = async (adjustmentId) => {
    setProcessing(adjustmentId);
    try {
      await dispatch(
        approvePriceAdjustmentThunk({
          sessionId,
          adjustmentId,
          adminCode: adminInfo.adminCode,
          adminName: adminInfo.adminName,
        })
      ).unwrap();

      toast.success("Approved");
      dispatch(calculatePricingThunk(sessionId));

      if (pendingAdjustments.length === 1) onClose();
    } catch (error) {
      toast.error("Failed to approve");
    }
    setProcessing(null);
  };

  const handleReject = async (adjustmentId) => {
    if (!rejectionReason.trim()) {
      toast.error("Reason required");
      return;
    }

    setProcessing(adjustmentId);
    try {
      await dispatch(
        rejectPriceAdjustmentThunk({
          sessionId,
          adjustmentId,
          rejectionReason,
          adminCode: adminInfo.adminCode,
          adminName: adminInfo.adminName,
        })
      ).unwrap();

      toast.success("Rejected");
      setRejectingId(null);
      setRejectionReason("");

      if (pendingAdjustments.length === 1) onClose();
    } catch (error) {
      toast.error("Failed to reject");
    }
    setProcessing(null);
  };

  const formatAdjustment = (adj) => {
    if (adj.adjustment.type === "PERCENTAGE") {
      return `${adj.adjustment.value}% off`;
    } else if (adj.adjustment.type === "FIXED_AMOUNT") {
      return `₹${adj.adjustment.value.toLocaleString()} off`;
    } else if (adj.adjustment.type === "OVERRIDE_PRICE") {
      return `Override to ₹${adj.adjustment.overridePrice.toLocaleString()}`;
    }
    return "";
  };

  const getApprovalReason = (adj) => {
    if (adj.adjustment.type === "PERCENTAGE" && adj.adjustment.value > 10) {
      return `${adj.adjustment.value}% exceeds 10% limit`;
    }
    if (adj.adjustment.type === "FIXED_AMOUNT" && adj.adjustment.value > 5000) {
      return `₹${adj.adjustment.value.toLocaleString()} exceeds ₹5,000 limit`;
    }
    if (adj.adjustment.type === "OVERRIDE_PRICE") {
      return "Price override requires approval";
    }
    if (
      adj.adjustmentType === "EXCHANGE_BONUS" &&
      adj.adjustment.value > 5000
    ) {
      return `Exchange bonus ₹${adj.adjustment.value.toLocaleString()} exceeds limit`;
    }
    return "Manager approval required";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <FiAlertCircle className="text-orange-500 text-xl" />
            <h2 className="text-lg font-semibold">
              Pending Approvals ({pendingAdjustments.length})
            </h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <FiX className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {pendingAdjustments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No pending adjustments
            </div>
          ) : (
            <div className="space-y-3">
              {pendingAdjustments.map((adj) => (
                <div
                  key={adj.adjustmentId}
                  className="border rounded-lg p-4 bg-yellow-50 border-yellow-200"
                >
                  {/* Main Info */}
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {adj.adjustmentType.replace(/_/g, " ")}
                      </p>
                      <p className="text-sm text-gray-600">
                        {formatAdjustment(adj)} • {adj.scope}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-orange-600">
                        {adj.adjustment.type === "PERCENTAGE"
                          ? `${adj.adjustment.value}%`
                          : `₹${(
                              adj.adjustment.value ||
                              adj.adjustment.overridePrice
                            ).toLocaleString()}`}
                      </p>
                      <p className="text-xs text-gray-500">
                        by {adj.createdBy}
                      </p>
                    </div>
                  </div>

                  {/* Reason */}
                  <div className="mb-3 p-2 bg-white rounded">
                    <p className="text-xs text-gray-500 mb-1">Reason:</p>
                    <p className="text-sm">{adj.reason}</p>
                  </div>

                  {/* Why Approval Required */}
                  <div className="mb-3 p-2 bg-red-50 rounded text-sm text-red-700">
                    ⚠️ {getApprovalReason(adj)}
                  </div>

                  {/* Actions */}
                  {rejectingId === adj.adjustmentId ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Enter rejection reason..."
                        className="w-full p-2 border rounded"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleReject(adj.adjustmentId)}
                          loading={processing === adj.adjustmentId}
                          className="flex-1"
                        >
                          Confirm Reject
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setRejectingId(null);
                            setRejectionReason("");
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => handleApprove(adj.adjustmentId)}
                        loading={processing === adj.adjustmentId}
                        disabled={processing}
                        className="flex-1"
                      >
                        <FiCheck className="mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRejectingId(adj.adjustmentId)}
                        disabled={processing}
                        className="flex-1"
                      >
                        <FiX className="mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdjustmentApprovalModal;
